'use client'
import React, { useState, useCallback, useEffect, useRef } from 'react'
import { useLocation } from 'react-router'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faFilter,
  faMagnifyingGlass,
  faRefresh,
  faAnglesLeft,
  faAnglesRight,
  faChevronLeft,
  faChevronRight,
} from '@fortawesome/pro-regular-svg-icons'
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  outlinedInputClasses,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table'
import Button from 'src/components/era-package/forms/Button'
import ChildCard from 'src/components/era-package/shared/ChildCard'
import { Filter } from 'src/components/era-package/filters'
import { optionsMap as fieldOptionsMap } from 'src/views/abi/fields/abi-query-list'
import { useServerPagination } from '../../hooks/useServerPagination'
import AbiQueryApiUrls from '../../../../apiUrls/AbiQueryApiUrls'

// Build columns with TanStack column helper
const columnHelper = createColumnHelper()

const resolveDescription = (value, optionKey) => {
  const options = fieldOptionsMap[optionKey]
  if (!options || !Array.isArray(options)) return String(value ?? '-')
  const match = options.find((o) => o.value === value)
  return match ? match.description : String(value ?? '-')
}

const queryListColumns = [
  columnHelper.accessor('queryType', {
    header: () => 'Query Type',
    cell: (info) => resolveDescription(info.getValue(), 'queryType'),
    filterFn: 'includesString',
  }),
  columnHelper.accessor('reference', {
    header: () => 'Reference',
    filterFn: 'includesString',
  }),
  columnHelper.accessor('status', {
    header: () => 'Status',
    filterFn: 'includesString',
  }),
  columnHelper.accessor('submittedTime', {
    header: () => 'Submitted Time',
    cell: (info) => {
      const v = info.getValue()
      return v ? new Date(v).toLocaleString() : '-'
    },
    filterFn: 'includesString',
  }),
  columnHelper.accessor('receivedTime', {
    header: () => 'Received Time',
    cell: (info) => {
      const v = info.getValue()
      return v ? new Date(v).toLocaleString() : '-'
    },
    filterFn: 'includesString',
  }),
]

const COLUMN_TO_PARAM = {
  queryType: 'queryType',
  reference: 'reference',
  status: 'status',
  submittedTime: 'submittedTime',
}

const QUERY_DEFAULT_PARAMS = {
  page: 0,
  size: 10,
  search: '',
  queryType: '',
  reference: '',
  status: '',
  submittedTime: '',
}

const QueryListTable = ({
  handleCustomEdit,
}) => {
  const theme = useTheme()
  const location = useLocation()

  const [searchInput, setSearchInput] = useState('')
  const [columnFilters, setColumnFilters] = useState([])
  const [showFilters, setShowFilters] = useState(false)
  const [sorting, setSorting] = useState([{ id: 'submittedTime', desc: true }])
  const debounceRef = useRef(null)
  const openQueryRef = useRef(location.state?.openQuery ?? null)

  const { data, totalElements, totalPages, loading, params, fetchPage, updateParams } =
    useServerPagination(AbiQueryApiUrls.LIST, QUERY_DEFAULT_PARAMS)

  // Deep-link from dashboard: filter by reference, then auto-open the matching response once loaded.
  useEffect(() => {
    if (openQueryRef.current?.reference) {
      updateParams({ reference: openQueryRef.current.reference })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const openQuery = openQueryRef.current
    if (!openQuery || loading || data.length === 0) return
    openQueryRef.current = null
    const match = data.find((row) => row.submittedTime === openQuery.submittedTime) || data[0]
    if (match && typeof handleCustomEdit === 'function') handleCustomEdit(match)
  }, [data, loading, handleCustomEdit])

  const pageIndex = params.page
  const pageSize = params.size

  const table = useReactTable({
    data,
    columns: queryListColumns,
    filterFns: {},
    state: {
      columnFilters,
      sorting,
      pagination: { pageIndex, pageSize },
    },
    manualPagination: true,
    pageCount: totalPages,
    onSortingChange: setSorting,
    onColumnFiltersChange: (updater) => {
      const next = typeof updater === 'function' ? updater(columnFilters) : updater
      setColumnFilters(next)
      const mapped = {}
      Object.values(COLUMN_TO_PARAM).forEach((f) => (mapped[f] = ''))
      next.forEach(({ id, value }) => {
        const key = COLUMN_TO_PARAM[id]
        if (key) mapped[key] = value ?? ''
      })
      updateParams({ ...mapped, page: 0 })
    },
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  })

  const handleRowClick = useCallback(
    (row) => {
      if (typeof handleCustomEdit === 'function') {
        handleCustomEdit(row)
      }
    },
    [handleCustomEdit],
  )

  return (
    <Stack spacing={2}>
      {/* Controls */}
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems="center"
        spacing={2}
        sx={{ mb: 2 }}
      >
                <TextField
          size="small"
          placeholder="Search all columns..."
          value={searchInput}
          onChange={(e) => {
            const value = e.target.value
            setSearchInput(value)
            clearTimeout(debounceRef.current)
            debounceRef.current = setTimeout(() => {
              updateParams({ search: value, page: 0 })
            }, 500)
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <FontAwesomeIcon icon={faMagnifyingGlass} style={{ width: 16, height: 16 }} />
              </InputAdornment>
            ),
          }}
          sx={{
            maxWidth: { xs: '100%', sm: '300px' },
            [`& .${outlinedInputClasses.notchedOutline}`]: { inset: 0 },
          }}
        />
        <Button
          variant="outlined"
          onClick={() => setShowFilters((prev) => !prev)}
          startIcon={<FontAwesomeIcon icon={faFilter} style={{ width: 16, height: 16 }} />}
        >
          {showFilters ? 'Hide Filters' : 'Show Column Filters'}
        </Button>
      </Stack>

      {/* Table */}
      <ChildCard sx={{ p: 0, mb: -3 }}>
        <Stack>
          <TableContainer>
            <Table>
              <TableHead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <React.Fragment key={headerGroup.id}>
                    <TableRow>
                      {headerGroup.headers.map((header) => (
                        <TableCell key={header.id}>
                          <Typography
                            variant="body"
                            color="primary.main"
                            fontWeight="600"
                            {...{
                              className: header.column.getCanSort()
                                ? 'cursor-pointer select-none'
                                : '',
                              onClick: header.column.getToggleSortingHandler(),
                            }}
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(header.column.columnDef.header, header.getContext())}
                            {{ asc: ' 🔼', desc: ' 🔽' }[header.column.getIsSorted()] ?? null}
                          </Typography>
                        </TableCell>
                      ))}
                    </TableRow>
                    {showFilters && (
                      <TableRow>
                        {headerGroup.headers.map((header) => (
                          <TableCell key={`filter-${header.id}`}>
                            {header.column.getCanFilter() ? (
                              <Filter column={header.column} />
                            ) : null}
                          </TableCell>
                        ))}
                      </TableRow>
                    )}
                  </React.Fragment>
                ))}
              </TableHead>
              <TableBody>
                {table.getRowModel().rows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    sx={{
                      cursor: 'pointer',
                      transition: (t) => t.transitions.create(),
                      '&:hover': { backgroundColor: 'action.hover' },
                    }}
                    onClick={() => handleRowClick(row.original)}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <TableCell key={cell.id}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                                {loading && (
                  <TableRow>
                    <TableCell colSpan={queryListColumns.length} align="center">
                      <Typography variant="body2" color="text.secondary">Loading...</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {!loading && data.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={queryListColumns.length} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No query records.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Divider />

          {/* Pagination & Refresh */}
          <Stack
            gap={1}
            p={3}
            alignItems="center"
            direction={{ xs: 'column', sm: 'row' }}
            justifyContent="space-between"
          >
                        <Box display="flex" alignItems="center" gap={1}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => fetchPage()}
                startIcon={
                  <FontAwesomeIcon
                    icon={faRefresh}
                    style={{ width: 16, height: 16 }}
                    spin={loading}
                  />
                }
              >
                Refresh data
              </Button>
              <Typography variant="body1">
                {totalElements} Rows
              </Typography>
            </Box>

            <Box
              sx={{ display: { xs: 'block', sm: 'flex' } }}
              alignItems="center"
              gap={1}
            >
                            <Stack direction="row" alignItems="center" gap={1}>
                <Typography variant="body1">Page</Typography>
                <Typography variant="body1" fontWeight={600}>
                  {pageIndex + 1} of {totalPages}
                </Typography>
              </Stack>
              <Stack direction="row" alignItems="center" gap={1}>
                | Go to page:
                                <TextField
                  type="number"
                  inputProps={{ min: 1, max: totalPages }}
                  defaultValue={pageIndex + 1}
                                                      onChange={(e) => {
                    const val = Number(e.target.value)
                    if (!val || val < 1) return
                    updateParams({ page: Math.min(val, totalPages) - 1 })
                  }}
                  size="small"
                />
              </Stack>
              <Select
                value={pageSize}
                onChange={(e) => updateParams({ size: Number(e.target.value), page: 0 })}
                size="small"
              >
                {[5, 10, 15, 20, 25].map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </Select>

              <IconButton
                size="small"
                onClick={() => updateParams({ page: 0 })}
                disabled={pageIndex === 0}
              >
                <FontAwesomeIcon icon={faAnglesLeft} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => updateParams({ page: pageIndex - 1 })}
                disabled={pageIndex === 0}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => updateParams({ page: pageIndex + 1 })}
                disabled={pageIndex >= totalPages - 1}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => updateParams({ page: totalPages - 1 })}
                disabled={pageIndex >= totalPages - 1}
              >
                <FontAwesomeIcon icon={faAnglesRight} />
              </IconButton>
            </Box>
          </Stack>
        </Stack>
      </ChildCard>
    </Stack>
  )
}

export default QueryListTable
